import { z } from 'zod';
import type { TFunction } from 'i18next';
import { DEFAULT_NAME_PATTERN } from '@/shared/config/validation';
import { isBefore, subYears } from 'date-fns';

const createEulaProfileFormSchema = (
  t: TFunction,
  skipEula: boolean = false
) => {
  const required = t('authPages.validation.requiredField');
  const allowedChars = t(
    'personalAccount.verification.eulaProfileForm.validationName'
  );
  const validationDateOfBirth = t(
    'personalAccount.verification.eulaProfileForm.validationDateOfBirth'
  );
  const validationDateOfBirthAge = t(
    'personalAccount.verification.eulaProfileForm.validationDateOfBirthAge'
  );

  const baseSchema = z.object({
    firstName: z
      .string()
      .nonempty({ message: required })
      .refine(val => new RegExp(`^[${DEFAULT_NAME_PATTERN}]+$`).test(val), {
        message: allowedChars,
      }),
    lastName: z
      .string()
      .nonempty({ message: required })
      .refine(val => new RegExp(`^[${DEFAULT_NAME_PATTERN}]+$`).test(val), {
        message: allowedChars,
      }),
    middleName: z
      .string()
      .optional()
      .refine(
        val => !val || new RegExp(`^[${DEFAULT_NAME_PATTERN}]+$`).test(val),
        {
          message: allowedChars,
        }
      ),
    dateOfBirth: z
      .string()
      .nonempty({ message: required })
      .refine(
        val => {
          // Check if the date matches dd.mm.yyyy format
          const dateRegex = /^\d{2}\.\d{2}\.\d{4}$/;
          if (!dateRegex.test(val)) return false;

          // Parse the date components
          const [day, month, year] = val.split('.').map(Number);

          // Validate date components
          if (month < 1 || month > 12) return false;
          if (day < 1 || day > 31) return false;

          // Create date object and check if it's valid
          const date = new Date(year, month - 1, day);
          return (
            date.getDate() === day &&
            date.getMonth() === month - 1 &&
            date.getFullYear() === year
          );
        },
        {
          message: validationDateOfBirth,
        }
      )
      .refine(
        val => {
          const [day, month, year] = val.split('.').map(Number);
          const birthDate = new Date(year, month - 1, day);
          const today = new Date();
          const eighteenYearsAgo = subYears(today, 18);

          return isBefore(birthDate, eighteenYearsAgo);
        },
        {
          message: validationDateOfBirthAge,
        }
      ),
    citizenship: z.string().nonempty({ message: required }),
  });

  if (skipEula) {
    return baseSchema;
  }

  return baseSchema.extend({
    checkBoxesResult: z.array(
      z.object({
        checkBoxId: z.string(),
        isSelected: z.boolean().refine(val => val === true, {
          message: required,
        }),
      })
    ),
  });
};

type EulaProfileFormValues = z.infer<
  ReturnType<typeof createEulaProfileFormSchema>
>;

export { createEulaProfileFormSchema, type EulaProfileFormValues };
